/**
 * Core
 */

import { ticketRoutes } from '@core/router/routeNames';

/**
 * Access
 */

import { ticketRoutePermissions } from '@modules/tickets/access/ticketRoutePermissions';

/**
 * Types
 */

import type { TicketAccessContext } from '@modules/tickets/access/ticketAccessContext';
import type { RoutesList } from '@core/router/routeNames';
import type { ValueOf } from '@shared/types/Helpers';

type TicketRoute = Exclude<
  ValueOf<typeof ticketRoutes>,
  typeof ticketRoutes.NEW_TICKET
>;

const reachableTicketRouteNames = new Set<string>(
  Object.values(ticketRoutes).filter(
    (route) => route !== ticketRoutes.NEW_TICKET,
  ),
);

function isReachableTicketRouteName(route: RoutesList): route is TicketRoute {
  return reachableTicketRouteNames.has(route);
}

/**
 * Whether the user may open this ticket route for the given access context.
 */
function isTicketRouteAllowed(
  context: TicketAccessContext,
  route: TicketRoute,
): boolean {
  const requiredPermission = ticketRoutePermissions[route];

  if (requiredPermission === null) {
    return context.isTicketUser;
  }

  return context.permissions?.includes(requiredPermission) ?? false;
}

export type { TicketRoute };

export { isReachableTicketRouteName, isTicketRouteAllowed };
